import {
  capitalize,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { FormikDimensionedNumberInput } from "common/components/FormikDimensionedNumberInput";
import { ImplementViewModel } from "../ImplementViewModel";
import { MuiErrorMessage } from "common/components/MuiErrorMessage";
import { PtoSpeed, PtoType } from "protos/atk/enums";
import {
  ptoSpeedToLocaleString,
  ptoTypeToLocationString,
} from "common/utils/enumTranslations";
import { TemplateOverride } from "../../TemplateOverride";
import { typedPath } from "typed-path";
import { useFormikContext } from "formik";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import React, { FC, useState } from "react";

const S = typedPath<ImplementViewModel>();

export const PtoRequirementsStep: FC<{ disabled?: boolean }> = ({
  disabled,
}) => {
  const { t } = useTranslation();
  const { measurementSystem } = useSelf();
  const formikProps = useFormikContext<ImplementViewModel>();

  const [checkedOverrideBox, setCheckedOverrideBox] = useState<boolean>(
    Boolean(formikProps.values.ptoRequirements)
  );

  const hasCustomInfo =
    Boolean(formikProps.values.ptoRequirements) || checkedOverrideBox;

  return (
    <div className="flex flex-col gap-2">
      <TemplateOverride
        checked={hasCustomInfo}
        disabled={disabled}
        onChange={() => {
          if (hasCustomInfo) {
            setCheckedOverrideBox(false);
            formikProps.setFieldValue(S.ptoRequirements.$path, undefined);
          } else {
            setCheckedOverrideBox(true);
            formikProps.setFieldValue(
              S.ptoRequirements.$path,
              formikProps.values.archetype?.ptoRequirements
            );
          }
        }}
      />
      {hasCustomInfo && (
        <Paper className="flex flex-col gap-2 p-4">
          <div className="flex flex-col">
            <Typography>
              {capitalize(t("utils.descriptors.type_one"))}
            </Typography>
            <FormControl
              disabled={
                formikProps.values.ptoRequirements === undefined || disabled
              }
            >
              <RadioGroup
                className="flex gap-2"
                row
                value={
                  formikProps.values.ptoRequirements?.type ??
                  PtoType.PTO_TYPE_NONE
                }
                onChange={(e) =>
                  formikProps.setFieldValue(
                    S.ptoRequirements.type.$path,
                    Number.parseInt(e.target.value)
                  )
                }
              >
                <FormControlLabel
                  value={PtoType.PTO_TYPE_FRONT}
                  control={<Radio />}
                  label={capitalize(
                    t(ptoTypeToLocationString(PtoType.PTO_TYPE_FRONT))
                  )}
                />
                <FormControlLabel
                  value={PtoType.PTO_TYPE_REAR}
                  control={<Radio />}
                  label={capitalize(
                    t(ptoTypeToLocationString(PtoType.PTO_TYPE_REAR))
                  )}
                />
                <FormControlLabel
                  value={PtoType.PTO_TYPE_FRONT_AND_REAR}
                  control={<Radio />}
                  label={capitalize(
                    t(ptoTypeToLocationString(PtoType.PTO_TYPE_FRONT_AND_REAR))
                  )}
                />
              </RadioGroup>
            </FormControl>
            <MuiErrorMessage name={S.ptoRequirements.ptoSpeed.$path} />
          </div>
          <div className="flex flex-col">
            <Typography>
              {capitalize(t("utils.descriptors.speed_one"))}
            </Typography>
            <FormControl
              disabled={
                formikProps.values.ptoRequirements === undefined || disabled
              }
            >
              <RadioGroup
                className="flex gap-2"
                row
                value={
                  formikProps.values.ptoRequirements?.ptoSpeed ??
                  PtoSpeed.PTO_SPEED_UNSPECIFIED
                }
                onChange={(e) =>
                  formikProps.setFieldValue(
                    S.ptoRequirements.ptoSpeed.$path,
                    Number.parseInt(e.target.value)
                  )
                }
              >
                <FormControlLabel
                  value={PtoSpeed.PTO_SPEED_540E}
                  control={<Radio />}
                  label={t(ptoSpeedToLocaleString(PtoSpeed.PTO_SPEED_540E))}
                />
                <FormControlLabel
                  value={PtoSpeed.PTO_SPEED_540}
                  control={<Radio />}
                  label={t(ptoSpeedToLocaleString(PtoSpeed.PTO_SPEED_540))}
                />
                <FormControlLabel
                  value={PtoSpeed.PTO_SPEED_1000E}
                  control={<Radio />}
                  label={t(ptoSpeedToLocaleString(PtoSpeed.PTO_SPEED_1000E))}
                />
                <FormControlLabel
                  value={PtoSpeed.PTO_SPEED_1000}
                  control={<Radio />}
                  label={t(ptoSpeedToLocaleString(PtoSpeed.PTO_SPEED_1000))}
                />
              </RadioGroup>
            </FormControl>
            <MuiErrorMessage name={S.ptoRequirements.type.$path} />
          </div>
          <FormikDimensionedNumberInput
            name={S.ptoRequirements.engineSpeedRpm.$path}
            label={capitalize(t("models.equipment.engineSpeed"))}
            dimensionedNumberInputProps={{
              storedUnit: "rpm",
              displayUnits: { imperial: "rpm", metric: "rpm" },
              localSystem: measurementSystem,
              disabled:
                formikProps.values.ptoRequirements === undefined || disabled,
            }}
          />
        </Paper>
      )}
      {!hasCustomInfo && (
        <Paper className="flex flex-col gap-2 p-4">
          <div className="flex flex-col">
            <Typography>
              {capitalize(t("utils.descriptors.type_one"))}
            </Typography>
            <FormControl disabled>
              <RadioGroup
                className="flex gap-2"
                row
                value={
                  formikProps.values.archetype?.ptoRequirements?.type ??
                  PtoType.PTO_TYPE_NONE
                }
              >
                <FormControlLabel
                  value={PtoType.PTO_TYPE_FRONT}
                  control={<Radio />}
                  label={capitalize(
                    t(ptoTypeToLocationString(PtoType.PTO_TYPE_FRONT))
                  )}
                />
                <FormControlLabel
                  value={PtoType.PTO_TYPE_REAR}
                  control={<Radio />}
                  label={capitalize(
                    t(ptoTypeToLocationString(PtoType.PTO_TYPE_REAR))
                  )}
                />
                <FormControlLabel
                  value={PtoType.PTO_TYPE_FRONT_AND_REAR}
                  control={<Radio />}
                  label={capitalize(
                    t(ptoTypeToLocationString(PtoType.PTO_TYPE_FRONT_AND_REAR))
                  )}
                />
              </RadioGroup>
            </FormControl>
            <MuiErrorMessage name={S.ptoRequirements.ptoSpeed.$path} />
          </div>
          <div className="flex flex-col">
            <Typography>
              {capitalize(t("utils.descriptors.speed_one"))}
            </Typography>
            <FormControl disabled>
              <RadioGroup
                className="flex gap-2"
                row
                value={
                  formikProps.values.archetype?.ptoRequirements?.ptoSpeed ??
                  PtoSpeed.PTO_SPEED_UNSPECIFIED
                }
              >
                <FormControlLabel
                  value={PtoSpeed.PTO_SPEED_540E}
                  control={<Radio />}
                  label={t(ptoSpeedToLocaleString(PtoSpeed.PTO_SPEED_540E))}
                />
                <FormControlLabel
                  value={PtoSpeed.PTO_SPEED_540}
                  control={<Radio />}
                  label={t(ptoSpeedToLocaleString(PtoSpeed.PTO_SPEED_540))}
                />
                <FormControlLabel
                  value={PtoSpeed.PTO_SPEED_1000E}
                  control={<Radio />}
                  label={t(ptoSpeedToLocaleString(PtoSpeed.PTO_SPEED_1000E))}
                />
                <FormControlLabel
                  value={PtoSpeed.PTO_SPEED_1000}
                  control={<Radio />}
                  label={t(ptoSpeedToLocaleString(PtoSpeed.PTO_SPEED_1000))}
                />
              </RadioGroup>
            </FormControl>
            <MuiErrorMessage name={S.ptoRequirements.type.$path} />
          </div>
          <FormikDimensionedNumberInput
            name={S.archetype.ptoRequirements.engineSpeedRpm.$path}
            label={capitalize(t("models.equipment.engineSpeed"))}
            dimensionedNumberInputProps={{
              storedUnit: "rpm",
              displayUnits: { imperial: "rpm", metric: "rpm" },
              localSystem: measurementSystem,
              disabled: true,
            }}
          />
        </Paper>
      )}
    </div>
  );
};
